import React from 'react';
import PropTypes from 'prop-types';

import {TableCell, TableRow, Link, IconButton, Tooltip, CircularProgress, Typography} from 'views/components';
import {Download as DownloadIcon, Refresh as RefreshIcon} from 'views/icons';
import {translate} from 'views/module';

import Backend from 'utils/backend';
import moment from 'moment';

const SyncCsvRow = ({item, statusProgressWrap}) => {
  const [fileUrl, setFileUrl] = React.useState(item.fileUrl);
  const [fileName, setFileName] = React.useState(item.fileName);
  const [isRegenerating, setIsRegenerating] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const onRegenerate = () => {
    statusProgressWrap(async () => {
      setIsRegenerating(true);
      setError(null);
      try {
        const result = await Backend.paymentSlip.regenerateCaregiverPaymentSyncCsv(item.weekOf);
        if (result.regenerated) {
          setFileUrl(result.fileUrl);
          setFileName(`${item.weekOf}.csv`);
        } else {
          setError(translate('dashboardWidgets.CaregiverPaymentSyncCsv.RegenerateEmpty'));
        }
      } catch (e) {
        setError(translate('dashboardWidgets.CaregiverPaymentSyncCsv.RegenerateError'));
      } finally {
        setIsRegenerating(false);
      }
    }, {showProgress: false, debugString: 'CAREGIVER_PAYMENT_SYNC_CSV_REGENERATE'});
  };

  const onDownload = () => {
    statusProgressWrap(async () => {
      setIsDownloading(true);
      setError(null);
      try {
        // The stored fileUrl is the plain CloudFront object location, which isn't directly
        // downloadable -- exchange it for a time-limited presigned S3 read URL first.
        const result = await Backend.fileStorage.downloadFile({src: fileUrl});
        window.open(result.signedUrl, '_blank', 'noreferrer');
      } catch (e) {
        setError(translate('dashboardWidgets.CaregiverPaymentSyncCsv.DownloadError'));
      } finally {
        setIsDownloading(false);
      }
    }, {showProgress: false, debugString: 'CAREGIVER_PAYMENT_SYNC_CSV_DOWNLOAD'});
  };

  return (
    <TableRow>
      <TableCell>
        {`${moment.utc(item.start).format('MMM D')} - ${moment.utc(item.end).format('MMM D, YYYY')}`}
      </TableCell>
      <TableCell>
        <Link component="button" type="button" onClick={onDownload} disabled={isDownloading}
          sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          {isDownloading ? <CircularProgress size={14} /> : <DownloadIcon fontSize="small" />}
          {fileName}
        </Link>
        {error && <Typography variant="caption" color="error">{error}</Typography>}
      </TableCell>
      <TableCell align="right">
        <Tooltip title={translate('dashboardWidgets.CaregiverPaymentSyncCsv.Regenerate')}>
          <span>
            <IconButton onClick={onRegenerate} disabled={isRegenerating} size="small">
              {isRegenerating ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

SyncCsvRow.propTypes = {
  item: PropTypes.shape({
    weekOf: PropTypes.string.isRequired,
    start: PropTypes.string.isRequired,
    end: PropTypes.string.isRequired,
    fileName: PropTypes.string.isRequired,
    fileUrl: PropTypes.string.isRequired,
  }).isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

export default React.memo(SyncCsvRow);
